// components/CustomGameModal.tsx
import React, { useState } from 'react';
import {
  IonModal,
  IonHeader,
  IonToolbar,
  IonTitle,
  IonButtons,
  IonButton,
  IonContent,
  IonList,
  IonItem,
  IonLabel,
  IonInput,
  IonSelect,
  IonSelectOption,
  IonNote,
} from '@ionic/react';
import { moderateContent } from "../services/contentModeration";

interface CustomGameModalProps {
  isOpen: boolean;
  onDismiss: () => void;
  onSave: (gameData: {
    name: string;
    game_type: 'boardgame' | 'rpg';
    min_players?: number;
    max_players?: number;
    year_published?: number;
  }) => void;
}

export const CustomGameModal: React.FC<CustomGameModalProps> = ({
  isOpen,
  onDismiss,
  onSave,
}) => {
  const [name, setName] = useState('');
  const [gameType, setGameType] = useState<'boardgame' | 'rpg'>('boardgame');
  const [minPlayers, setMinPlayers] = useState<number | undefined>();
  const [maxPlayers, setMaxPlayers] = useState<number | undefined>();
  const [yearPublished, setYearPublished] = useState<number | undefined>();
  const [errorMessage, setErrorMessage] = useState("");

  const handleSave = () => {
    if (!name.trim()) {
      setErrorMessage('Game name is required');
      return;
    }

    // Content moderation - block hate speech in custom game names
    const moderationCheck = moderateContent(name);
    if (!moderationCheck.isClean) {
      setErrorMessage('Game name contains inappropriate content');
      return;
    }

    setErrorMessage('');

    onSave({
      name: name.trim(),
      game_type: gameType,
      min_players: minPlayers,
      max_players: maxPlayers,
      year_published: yearPublished,
    });

    // Reset form
    setName('');
    setGameType('boardgame');
    setMinPlayers(undefined);
    setMaxPlayers(undefined);
    setYearPublished(undefined);
  };

  const handleCancel = () => {
    // Reset form
    setName('');
    setGameType('boardgame');
    setMinPlayers(undefined);
    setMaxPlayers(undefined);
    setYearPublished(undefined);
    onDismiss();
  };

  return (
    <IonModal isOpen={isOpen} onDidDismiss={handleCancel}>
      <IonHeader>
        <IonToolbar>
          <IonTitle>Add Custom Game</IonTitle>
          <IonButtons slot="start">
            <IonButton onClick={handleCancel}>Cancel</IonButton>
          </IonButtons>
          <IonButtons slot="end">
            <IonButton onClick={handleSave} strong disabled={!name.trim()}>
              Add
            </IonButton>
          </IonButtons>
        </IonToolbar>
      </IonHeader>

      <IonContent>
        <IonList>
          <IonItem>
            <IonLabel position="stacked">
              Game Name *
            </IonLabel>
            <IonInput
              value={name}
              onIonInput={(e) => setName(e.detail.value!)}
              placeholder="Enter the game name"
              maxlength={100}
            />
          </IonItem>
          {errorMessage && (
            <IonItem color="danger">
              <IonLabel className="ion-text-wrap">{errorMessage}</IonLabel>
            </IonItem>
          )}

          <IonItem>
            <IonLabel>Game Type *</IonLabel>
            <IonSelect
              value={gameType}
              onIonChange={(e) => setGameType(e.detail.value)}
            >
              <IonSelectOption value="boardgame">Board Game</IonSelectOption>
              <IonSelectOption value="rpg">RPG / TTRPG</IonSelectOption>
            </IonSelect>
          </IonItem>

          <IonItem>
            <IonLabel position="stacked">
              Min Players (Optional)
            </IonLabel>
            <IonInput
              type="number"
              value={minPlayers}
              onIonInput={(e) => setMinPlayers(e.detail.value ? parseInt(e.detail.value) : undefined)}
              placeholder="1"
              min="1"
              max="99"
            />
          </IonItem>

          <IonItem>
            <IonLabel position="stacked">
              Max Players (Optional)
            </IonLabel>
            <IonInput
              type="number"
              value={maxPlayers}
              onIonInput={(e) => setMaxPlayers(e.detail.value ? parseInt(e.detail.value) : undefined)}
              placeholder="4"
              min="1"
              max="99"
            />
          </IonItem>

          <IonItem>
            <IonLabel position="stacked">
              Year Published (Optional)
            </IonLabel>
            <IonInput
              type="number"
              value={yearPublished}
              onIonInput={(e) => setYearPublished(e.detail.value ? parseInt(e.detail.value) : undefined)}
              placeholder="2024"
              min="1900"
              max={new Date().getFullYear() + 1}
            />
          </IonItem>

          <IonItem lines="none">
            <IonNote style={{ fontSize: '13px', marginTop: '12px' }}>
              <strong>Note:</strong> Custom games are stored in your profile. Only exact name matches
              will count toward compatibility with other users. For best results, use the full official
              game name.
            </IonNote>
          </IonItem>
        </IonList>
      </IonContent>
    </IonModal>
  );
};